import api from '../utils/api';
import { normalizeApiResponse } from '../utils/apiResponseHandler';

const memberService = {
  // Super Admin dataroom user methods (based on Postman collection)
  superAdminDataroom: {
    // Get all users for a dataroom (list method for DataroomMembers component)
    list: async (dataroomUuid, params = {}) => {
      try {
        const queryParams = new URLSearchParams();
        // Add query parameters if provided
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
        if (params.role_id) queryParams.append('role_id', params.role_id);
        if (params.search) queryParams.append('search', params.search);
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        
        const response = await api.get(`/admin/datarooms/${dataroomUuid}/users?${queryParams.toString()}`);
        
        // Handle direct array response
        const data = response.data || response;
        const members = Array.isArray(data) ? data : (data.dataroom_users || data.users || data.data || []);
        
        // Calculate counts
        const activeCount = members.filter(u => u.is_active === 1 || u.is_active === true).length;
        const inactiveCount = members.filter(u => u.is_active === 0 || u.is_active === false).length;
        
        return {
          members: members,
          summary: {
            total: members.length,
            active: activeCount,
            inactive: inactiveCount
          },
          pagination: data.pagination || {
            current_page: params.page || 1,
            per_page: params.per_page || 15,
            total: members.length,
            last_page: Math.ceil(members.length / (params.per_page || 15))
          }
        };
      } catch (error) {
        throw error;
      }
    },

    // Get all users for a dataroom (original method)
    getUsers: async (dataroomUuid, params = {}) => {
      return memberService.superAdminDataroom.list(dataroomUuid, params);
    },

    // Get user detail
    getUserDetail: async (dataroomUuid, userId) => {
      try {
        const response = await api.get(`/admin/datarooms/${dataroomUuid}/users/${userId}`);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Update user role (for DataroomMembers component)
    updateRole: async (dataroomUuid, userId, roleId) => {
      try {
        const response = await api.put(`/admin/datarooms/${dataroomUuid}/users/${userId}`, {
          dataroom_role_id: roleId
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Update user permissions (for DataroomMembers component)
    updatePermissions: async (dataroomUuid, userId, permissionIds) => {
      try {
        const response = await api.put(`/admin/datarooms/${dataroomUuid}/users/${userId}`, {
          custom_permissions: permissionIds
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Update user (role and permissions)
    updateUser: async (dataroomUuid, userId, data) => {
      try {
        const response = await api.put(`/admin/datarooms/${dataroomUuid}/users/${userId}`, {
          dataroom_role_id: data.dataroom_role_id,
          custom_permissions: data.custom_permissions
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Toggle user status (active/inactive)
    toggleUserStatus: async (dataroomUuid, userId) => {
      try {
        const response = await api.post(`/admin/datarooms/${dataroomUuid}/users/${userId}/toggle-status`);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Remove user from dataroom (for DataroomMembers component)
    remove: async (dataroomUuid, userId) => {
      try {
        const response = await api.delete(`/admin/datarooms/${dataroomUuid}/users/${userId}`);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Delete user from dataroom (alias)
    deleteUser: async (dataroomUuid, userId) => {
      return memberService.superAdminDataroom.remove(dataroomUuid, userId);
    }
  },

  // Admin dataroom user methods (based on Postman collection "Dataroom Users - Admin")
  adminDataroom: {
    // Get all users for a dataroom
    list: async (dataroomUuid, params = {}) => {
      try {
        const queryParams = new URLSearchParams();
        // Add query parameters if provided
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
        if (params.role_id) queryParams.append('role_id', params.role_id);
        if (params.search) queryParams.append('search', params.search);
        if (params.page) queryParams.append('page', params.page);
        if (params.per_page) queryParams.append('per_page', params.per_page);
        
        const response = await api.get(`/datarooms/${dataroomUuid}/users?${queryParams.toString()}`);
        
        // Handle direct array response
        const data = response.data || response;
        const members = Array.isArray(data) ? data : (data.dataroom_users || data.users || data.data || []);
        
        // Calculate counts
        const activeCount = members.filter(u => u.is_active === 1 || u.is_active === true).length;
        const inactiveCount = members.filter(u => u.is_active === 0 || u.is_active === false).length;
        
        return {
          members: members,
          summary: {
            total: members.length,
            active: activeCount,
            inactive: inactiveCount
          },
          pagination: data.pagination || {
            current_page: params.page || 1,
            per_page: params.per_page || 15,
            total: members.length,
            last_page: Math.ceil(members.length / (params.per_page || 15))
          }
        };
      } catch (error) {
        throw error;
      }
    },

    // Get user detail
    getUserDetail: async (dataroomUuid, userId) => {
      try {
        const response = await api.get(`/datarooms/${dataroomUuid}/users/${userId}`);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Update user (role and permissions)
    updateUser: async (dataroomUuid, userId, data) => {
      try {
        const response = await api.put(`/datarooms/${dataroomUuid}/users/${userId}`, {
          dataroom_role_id: data.dataroom_role_id,
          custom_permissions: data.custom_permissions
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Update user role
    updateRole: async (dataroomUuid, userId, roleId) => {
      try {
        const response = await api.put(`/datarooms/${dataroomUuid}/users/${userId}`, {
          dataroom_role_id: roleId
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Update user permissions
    updatePermissions: async (dataroomUuid, userId, permissionIds) => {
      try {
        const response = await api.put(`/datarooms/${dataroomUuid}/users/${userId}`, {
          custom_permissions: permissionIds
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Toggle user status (active/inactive)
    toggleUserStatus: async (dataroomUuid, userId) => {
      try {
        const response = await api.post(`/datarooms/${dataroomUuid}/users/${userId}/toggle-status`);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Remove user from dataroom
    remove: async (dataroomUuid, userId) => {
      try {
        const response = await api.delete(`/datarooms/${dataroomUuid}/users/${userId}`);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // Delete user from dataroom (alias)
    deleteUser: async (dataroomUuid, userId) => {
      return memberService.adminDataroom.remove(dataroomUuid, userId);
    }
  }
};

export default memberService;